import React from 'react';

import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';

import { AmsRefKind, IntegrationType, useAmsBrowseCapabilitiesQuery } from '~/api/graphql';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import { useDebouncedValue } from '~/hooks/use-list-controls';
import {
  type Account,
  type AccountFilters,
  type AccountPick,
  type AccountTab,
  ALL_INTEGRATIONS,
  type Capability,
  type IsSelected,
  NO_ACCOUNT_FILTERS,
  NO_FILE_FILTERS,
  type Picked,
  type Policy,
  type SearchTab,
  type Toggle,
  type View
} from './ams-browser-model';
import { AccountView, PolicyView } from './ams-browser-record';
import { readRecents, withRecents, writeRecents } from './ams-browser-recents';
import { SearchView } from './ams-browser-search';
import type {
  AmsContextPinSelection,
  AmsDocumentPickerProps,
  AmsDocumentSelection
} from './ams-picker-types';
import { amsRefKey, entityTypeOfKind } from './ams-ref';
import { formatSelectedEntitySummary } from './entity-display';
import { RailListbox, SearchRail } from './picker-ui';

export type AmsBrowserProps = AmsDocumentPickerProps & {
  /**
   * Stops at the search stage and hands one account back, for a caller that
   * wants the account itself rather than records inside it. Nothing past the
   * accounts list renders, and the account is the row's own action.
   */
  onSelectAccount?: (account: AccountPick) => void;
};

/**
 * A CRM account mirrors an AMS account rather than being one, so it is not
 * something a client can resolve to. Mirrors the omission in
 * amsIntegrationCapabilities (pkg/service/api/api/ams_capabilities.go), which
 * is what refuses the link if one is offered anyway.
 */
const holdsLinkableAccounts = (capability: Capability): boolean =>
  capability.integrationType !== IntegrationType.CrmAgencyZoom &&
  capability.integrationType !== IntegrationType.CrmHubspot;

/**
 * The AMS browser: searches each of the agency's AMS integrations in parallel,
 * for accounts and for policies by number, then drills into one account's
 * policies, certificates and files. Records are picked by their AmsRef.
 */
export function AmsBrowser(props: AmsBrowserProps) {
  const { open, onClose, onConfirm, onSelectAccount } = props;
  const accountOnly = !!onSelectAccount;
  const userId = useOptionalCurrentAgencyUser()?.id;
  const capabilities = useAmsBrowseCapabilitiesQuery({ skip: !open });
  const browsable = capabilities.data?.amsBrowseCapabilities ?? [];
  const integrations = accountOnly ? browsable.filter(holdsLinkableAccounts) : browsable;

  const [searchQuery, setSearchQuery] = React.useState('');
  const [integrationFilter, setIntegrationFilter] = React.useState(ALL_INTEGRATIONS);
  // 'auto' follows the results until the user picks a tab for this search.
  const [searchTab, setSearchTab] = React.useState<SearchTab | 'auto'>('auto');
  // An account's filters stay with that account while a policy of it is open.
  const [accountFilters, setAccountFilters] = React.useState({
    account: '',
    filters: NO_ACCOUNT_FILTERS
  });
  const [policyFilters, setPolicyFilters] = React.useState(NO_FILE_FILTERS);
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), 300);
  const [view, setView] = React.useState<View>({ kind: 'search' });
  const [selected, setSelected] = React.useState<Map<string, Picked>>(new Map());
  const [recents, setRecents] = React.useState(() => readRecents(userId));

  // A caller that keeps the dialog mounted reopens it on a fresh search.
  React.useEffect(() => {
    if (!open) return;
    setSearchQuery('');
    setIntegrationFilter(ALL_INTEGRATIONS);
    setView({ kind: 'search' });
    setRecents(readRecents(userId));
  }, [open, userId]);

  React.useEffect(() => setSearchTab('auto'), [debouncedSearch, integrationFilter]);

  // Earlier browser picks come back selected once their integrations are known.
  React.useEffect(() => {
    if (!open) return;
    const restored = new Map<string, Picked>();
    for (const pick of [
      ...(props.initialSelectedFiles ?? []),
      ...(props.initialContextPins ?? [])
    ]) {
      const capability = integrations.find((c) => c.integrationId === pick.integrationId);
      if (pick.ref && capability) {
        restored.set(amsRefKey(pick.ref), {
          ref: pick.ref,
          displayName: pick.displayName,
          capability,
          accountName: pick.accountLabel
        });
      }
    }
    setSelected(restored);
    // The initial picks are read once per open, after the capabilities arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, integrations.length]);

  const toggle: Toggle = (pick) =>
    setSelected((previous) => {
      const next = new Map(previous);
      const key = amsRefKey(pick.ref);
      if (next.has(key)) next.delete(key);
      else next.set(key, pick);
      return next;
    });
  const isSelected: IsSelected = (ref) => selected.has(amsRefKey(ref));

  // A knowledge-graph pick, or a browser pick whose integration the browser
  // does not list, cannot be shown here; it passes through as it was.
  const restorable = (pick: { ref?: unknown; integrationId: string }) =>
    !!pick.ref && integrations.some((c) => c.integrationId === pick.integrationId);
  const keptFiles = (props.initialSelectedFiles ?? []).filter((pick) => !restorable(pick));
  const keptPins = (props.initialContextPins ?? []).filter((pick) => !restorable(pick));
  const pickedTypes = [
    ...keptFiles.map(() => ({ entityType: entityTypeOfKind(AmsRefKind.File) })),
    ...keptPins,
    ...[...selected.values()].map((pick) => ({ entityType: entityTypeOfKind(pick.ref.kind) }))
  ];

  const handleConfirm = () => {
    const files: AmsDocumentSelection[] = [...keptFiles];
    const contextPins: AmsContextPinSelection[] = [...keptPins];
    for (const [key, pick] of selected) {
      const base = {
        id: key,
        integrationId: pick.ref.integrationId,
        integrationType: pick.capability.integrationType,
        displayName: pick.displayName,
        accountId: pick.ref.accountExternalId ?? pick.ref.externalId,
        accountLabel: pick.accountName,
        ref: pick.ref
      };
      if (pick.ref.kind === AmsRefKind.File) files.push(base);
      else contextPins.push({ ...base, entityType: entityTypeOfKind(pick.ref.kind) });
    }
    writeRecents(
      userId,
      withRecents(
        recents,
        debouncedSearch ? [debouncedSearch] : [],
        [...selected.values()].map(({ ref, displayName, accountName }) => ({
          ref,
          displayName,
          accountName
        }))
      )
    );
    onConfirm({ files, contextPins });
    onClose();
  };

  const openAccount = (capability: Capability, account: Account, tab: AccountTab = 'policies') => {
    const key = amsRefKey(account.ref);
    if (accountFilters.account !== key) {
      setAccountFilters({ account: key, filters: NO_ACCOUNT_FILTERS });
    }
    setView({ kind: 'account', capability, account, tab });
  };
  const openPolicy = (
    capability: Capability,
    account: Account,
    policy: Policy,
    backTo: 'search' | 'account'
  ) => {
    setPolicyFilters(NO_FILE_FILTERS);
    setView({ kind: 'policy', capability, account, policy, backTo });
  };

  const searched =
    integrationFilter === ALL_INTEGRATIONS
      ? integrations
      : integrations.filter((c) => c.integrationId === integrationFilter);

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <div className="flex items-center justify-between gap-3">
        <DialogTitle>{accountOnly ? 'Link an AMS account' : 'Select AMS context'}</DialogTitle>
        {view.kind !== 'search' && integrations.length > 1 && (
          <div className="shrink-0 text-xs text-neutral-500 dark:text-neutral-400">
            {view.capability.displayName}
          </div>
        )}
      </div>

      <DialogBody className="mt-4">
        {view.kind === 'search' && (
          <>
            <SearchRail
              query={searchQuery}
              onQueryChange={setSearchQuery}
              placeholder={
                accountOnly
                  ? 'Search accounts by name, email, phone or lookup code'
                  : 'Search accounts by name, email, phone or lookup code, or a policy number'
              }
              ariaLabel="Search AMS accounts"
              trailing={
                integrations.length > 1 && (
                  <RailListbox
                    value={integrationFilter}
                    onChange={setIntegrationFilter}
                    ariaLabel="AMS to search"
                    options={[
                      { value: ALL_INTEGRATIONS, label: 'All AMS' },
                      ...integrations.map((c) => ({
                        value: c.integrationId,
                        label: c.displayName
                      }))
                    ]}
                  />
                )
              }
            />
            <SearchView
              capabilities={{
                loading: capabilities.loading,
                failed: !!capabilities.error && !capabilities.data,
                integrations
              }}
              searched={searched}
              query={debouncedSearch}
              tab={searchTab}
              onTab={setSearchTab}
              recents={recents}
              isSelected={isSelected}
              onToggle={toggle}
              onRecentQuery={setSearchQuery}
              onOpenAccount={(capability, account) => openAccount(capability, account)}
              onOpenPolicy={(capability, account, policy) =>
                openPolicy(capability, account, policy, 'search')
              }
              onPickAccount={
                onSelectAccount &&
                ((capability, account) => {
                  onSelectAccount({
                    integrationId: account.ref.integrationId,
                    integrationType: capability.integrationType,
                    externalId: account.ref.externalId,
                    displayName: account.name
                  });
                  onClose();
                })
              }
            />
          </>
        )}

        {view.kind === 'account' && (
          <AccountView
            key={amsRefKey(view.account.ref)}
            capability={view.capability}
            account={view.account}
            tab={view.tab}
            onTab={(tab) => setView({ ...view, tab })}
            filters={accountFilters.filters}
            onFilters={(filters: AccountFilters) =>
              setAccountFilters({ account: amsRefKey(view.account.ref), filters })
            }
            onBack={() => setView({ kind: 'search' })}
            isSelected={isSelected}
            onToggle={toggle}
            onOpenPolicy={(policy) => openPolicy(view.capability, view.account, policy, 'account')}
          />
        )}

        {view.kind === 'policy' && (
          <PolicyView
            key={amsRefKey(view.policy.ref)}
            capability={view.capability}
            account={view.account}
            policy={view.policy}
            filters={policyFilters}
            onFilters={setPolicyFilters}
            onBack={() =>
              view.backTo === 'search'
                ? setView({ kind: 'search' })
                : openAccount(view.capability, view.account)
            }
            backLabel={view.backTo === 'search' ? 'Back to policy search' : 'Back to account'}
            onOpenAccount={() => openAccount(view.capability, view.account)}
            isSelected={isSelected}
            onToggle={toggle}
          />
        )}
      </DialogBody>

      <DialogActions className="mt-4">
        <Button type="button" color="white" onClick={onClose}>
          Cancel
        </Button>
        {!accountOnly && (
          <>
            <Button
              type="button"
              color="sky"
              onClick={handleConfirm}
              disabled={selected.size === 0}
            >
              Add selected
            </Button>
            {pickedTypes.length > 0 && (
              // Last, so the column the footer stacks into on a phone puts it
              // above the buttons; beside them it moves to the left.
              <p
                data-testid="ams-browser-selection-summary"
                className="text-center text-xs font-medium text-sky-700 sm:order-first sm:mr-auto sm:text-left dark:text-sky-300"
              >
                {formatSelectedEntitySummary(pickedTypes)} selected
              </p>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
